import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert,
  CircularProgress, InputAdornment, IconButton, Divider, Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { login, isLogged, isAdmin, getUser } from '../services/authApi';
import TurnstileWidget from '../components/TurnstileWidget';

export default function LoginEcolab() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [tsToken, setTsToken]   = useState('');

  // Si ya está logueado, redirigir
  useEffect(() => {
    if (isLogged()) {
      window.location.href = isAdmin() ? '/admin/dashboard' : '/mi-portal';
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError('Ingrese email y contraseña.'); return; }
    if (!tsToken) { setError('Espere a que se complete la verificación de seguridad.'); return; }
    setLoading(true); setError('');
    try {
      await login(email.trim(), password, tsToken);
      const user = getUser();
      const roles = user?.roles || [];
      const esAdmin = roles.some(r => ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa'].includes(r));
      window.location.href = esAdmin ? '/admin/dashboard' : '/mi-portal';
    } catch (err) {
      setError(err?.data?.mensaje || 'Credenciales inválidas. Verifique su correo y contraseña.');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a2414 0%, #1a4d2e 45%, #2e7d32 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2, position: 'relative',
    }}>
      {/* Fondo decorativo */}
      <Box sx={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none' }}>
        {[...Array(6)].map((_,i) => (
          <Box key={i} sx={{
            position:'absolute', borderRadius:'50%',
            width: [300,200,400,150,250,180][i], height:[300,200,400,150,250,180][i],
            top:  ['10%','60%','30%','80%','5%','50%'][i],
            left: ['5%','70%','50%','20%','80%','35%'][i],
            background: 'rgba(255,255,255,0.025)',
            filter: 'blur(1px)',
          }}/>
        ))}
      </Box>

      <Box sx={{ width:'100%', maxWidth:420, position:'relative', zIndex:1 }}>
        {/* Botón volver */}
        <Button startIcon={<ArrowBackIcon />} href="/"
          sx={{ color:'rgba(255,255,255,0.7)', mb:2, '&:hover':{color:'#fff'} }}>
          Volver al sitio
        </Button>

        <Paper elevation={0} sx={{ borderRadius:3, overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,0.4)' }}>
          {/* Header */}
          <Box sx={{
            background:'linear-gradient(135deg,#1a4d2e,#2e7d32)',
            px:4, pt:4, pb:3, textAlign:'center',
          }}>
            <Box sx={{
              width:64, height:64, borderRadius:'50%', mx:'auto', mb:2,
              background:'rgba(255,255,255,0.15)',
              display:'flex', alignItems:'center', justifyContent:'center',
              border:'2px solid rgba(255,255,255,0.3)',
            }}>
              <LockOutlinedIcon sx={{ color:'#c8a84b', fontSize:32 }} />
            </Box>
            <Typography variant="h6" sx={{ color:'#fff', fontWeight:700 }}>
              Portal Alianza Solidaria
            </Typography>
            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.65)' }}>
              FENAGRO APL · Economías Colaborativas
            </Typography>
          </Box>

          {/* Barra dorada */}
          <Box sx={{ height:3, background:'linear-gradient(90deg,#c8a84b,#f0d070,#c8a84b)' }} />

          {/* Formulario */}
          <Box component="form" onSubmit={handleSubmit} sx={{ px:4, py:3.5 }}>
            <Typography variant="body2" sx={{ color:'#555', mb:3, textAlign:'center' }}>
              Ingrese con su correo registrado
            </Typography>

            <TextField fullWidth label="Correo electrónico" type="email" size="small"
              value={email} onChange={e => setEmail(e.target.value)} required
              sx={{ mb:2 }}
              InputProps={{ startAdornment:(
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ fontSize:18, color:'#aaa' }} />
                </InputAdornment>
              )}}
            />

            <TextField fullWidth label="Contraseña" size="small"
              type={showPass ? 'text' : 'password'}
              value={password} onChange={e => setPassword(e.target.value)} required
              sx={{ mb: error ? 2 : 3 }}
              InputProps={{
                endAdornment:(
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPass(p => !p)}>
                      {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb:2, fontSize:'0.8rem' }}>{error}</Alert>
            )}

            <TurnstileWidget onToken={setTsToken} action="login" />

            <Button fullWidth type="submit" variant="contained" disabled={loading} size="large"
              endIcon={loading && <CircularProgress size={18} color="inherit" />}
              sx={{
                py:1.4, borderRadius:2, fontWeight:700, letterSpacing:'0.05em',
                background:'linear-gradient(135deg,#1a4d2e,#43a047)',
                '&:hover':{ background:'linear-gradient(135deg,#0e2a18,#2e7d32)' },
              }}>
              {loading ? 'Ingresando…' : 'Ingresar'}
            </Button>

            <Box sx={{ textAlign:'center', mt:1.5 }}>
              <a href="/recuperar-password"
                style={{ color:'#2e7d32', textDecoration:'none', fontSize:'0.85rem', fontWeight:600 }}>
                ¿Olvidó su contraseña?
              </a>
            </Box>

            <Divider sx={{ my:2.5 }}>
              <Typography variant="caption" sx={{ color:'#bbb' }}>¿Nuevo aquí?</Typography>
            </Divider>

            <Button fullWidth variant="outlined" href="/afiliacion" color="success"
              sx={{ borderRadius:2, fontWeight:600 }}>
              Solicitar afiliación
            </Button>

            <Typography variant="caption" sx={{ display:'block', textAlign:'center', color:'#bbb', mt:2 }}>
              ¿Problemas para ingresar?{' '}
              <a href="mailto:fondoemprendimientofenagro@gmail.com"
                style={{ color:'#2e7d32', textDecoration:'none' }}>
                Contáctenos
              </a>
            </Typography>
          </Box>
        </Paper>

        <Typography variant="caption" sx={{ display:'block', textAlign:'center', color:'rgba(255,255,255,0.3)', mt:2 }}>
          FENAGRO APL · NIT 901026305-2
        </Typography>
      </Box>
    </Box>
  );
}
